import { Atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import {
  filteredBoatsResponseAtom,
  filterQueryStringAtom,
  initialRouterStateAtom,
  isQuerySynchronizationDoneAtom,
  isRouterReadyAtom,
  mergeExistingQueryToStateAtom,
  previousBoatResponseDataAtom,
  resetAllFiltersAtom,
} from './filterStore';

// sync prev boats response so it doesnt flash on loading state change
export const useSyncPrevBoatsResponse = () => {
  const setPrevBoatsResponse = useSetAtom(previousBoatResponseDataAtom);
  const filteredBoatsResponse = useAtomValue(filteredBoatsResponseAtom);

  useEffect(() => {
    if (filteredBoatsResponse.state === 'hasData') {
      setPrevBoatsResponse(filteredBoatsResponse);
    }
  }, [filteredBoatsResponse]);
};

// sync - push filter state to query onMount
export const useSyncPushFilterStateToQuery = (router) => {
  const filterQueryString = useAtomValue<string>(filterQueryStringAtom as Atom<string>);

  const isRouterReady = useAtomValue(isRouterReadyAtom);

  useEffect(() => {
    if (isRouterReady && filterQueryString) {
      if (decodeURIComponent(window.location.search).replace('?', '') === filterQueryString) return;

      router.push({ search: `?${filterQueryString}` }, null, { shallow: true });
    }
  }, [filterQueryString]);
};

const groupParamsByKey = (params) =>
  [...params.entries()].reduce((acc, tuple) => {
    // getting the key and value from each tuple
    const [key, val] = tuple;
    // eslint-disable-next-line no-prototype-builtins
    if (acc.hasOwnProperty(key)) {
      // if the current key is already an array, we'll add the value to it
      if (Array.isArray(acc[key])) {
        acc[key] = [...acc[key], val];
      } else {
        // if it's not an array, but contains a value, we'll convert it into an array
        // and add the current value to it
        acc[key] = [acc[key], val];
      }
    } else {
      // plain assignment if no special case is present
      acc[key] = val;
    }

    return acc;
  }, {});

export const useLoadFilterStateFromQuery = (router) => {
  const setIsRouterReady = useSetAtom(isRouterReadyAtom);
  const mergeExistingQueryToState = useSetAtom(mergeExistingQueryToStateAtom);
  const resetAllFilters = useSetAtom(resetAllFiltersAtom);
  const setInitialRouterState = useSetAtom(initialRouterStateAtom);
  const setQuerySynchronizationDone = useSetAtom(isQuerySynchronizationDoneAtom);

  useEffect(() => {
    // Sync history - browser back and forth navigation
    router.beforePopState(({ as }) => {
      const searchParams = new URLSearchParams(as.split('?')[1]);
      const query = groupParamsByKey(searchParams);

      resetAllFilters();
      mergeExistingQueryToState(query);

      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [mergeExistingQueryToState]);

  useEffect(() => {
    if (router.isReady) {
      // Retrieve filter state from query when router ready
      mergeExistingQueryToState(router.query);
      setInitialRouterState(router.query);
      setIsRouterReady(router.isReady);
      setTimeout(() => {
        setQuerySynchronizationDone(true);
      }, 1);
    }
  }, [router.isReady]);
};
